// Local File Storage Service using IndexedDB
// Completely free, no external services, works offline

class LocalFileStorage {
  constructor() {
    this.dbName = 'ProgressCraftFiles';
    this.dbVersion = 1;
    this.storeName = 'files';
    this.db = null;
  }

  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'id'
          });
          
          // Create indexes for efficient querying
          objectStore.createIndex('userId', 'userId', { unique: false });
          objectStore.createIndex('parayanaId', 'parayanaId', { unique: false });
          objectStore.createIndex('uploadedAt', 'uploadedAt', { unique: false });
          objectStore.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  async uploadFile(file, userId, parayanaId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        const fileData = {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userId,
          parayanaId,
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result, // Base64 data
          uploadedAt: new Date(),
          storagePath: `local/${userId}/${parayanaId}/${file.name}`
        };

        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.add(fileData);

        request.onsuccess = () => {
          // Return file metadata (without data for efficiency)
          const { data, ...metadata } = fileData;
          resolve({
            ...metadata,
            url: this.createFileURL(fileData.id)
          });
        };

        request.onerror = () => {
          reject(new Error('Failed to store file'));
        };
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  async getFiles(userId, parayanaId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const index = objectStore.index('parayanaId');
      const request = index.getAll(parayanaId);

      request.onsuccess = () => {
        const files = request.result
          .filter(file => file.userId === userId)
          .map(file => {
            const { data, ...metadata } = file;
            return {
              ...metadata,
              url: this.createFileURL(file.id)
            };
          });
        
        resolve(files);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve files'));
      };
    });
  }

  async getFileData(fileId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.get(fileId);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          reject(new Error('File not found'));
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve file'));
      };
    });
  }

  async deleteFile(fileId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.delete(fileId);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to delete file'));
      };
    });
  }

  createFileURL(fileId) {
    // Create a custom URL scheme for local files
    return `progresscraft://file/${fileId}`;
  }

  async resolveFileURL(url) {
    if (!url.startsWith('progresscraft://file/')) {
      return url; // Regular URL, return as-is
    }

    const fileId = url.replace('progresscraft://file/', '');
    try {
      const fileData = await this.getFileData(fileId);
      return fileData.data; // Return base64 data URL
    } catch (error) {
      console.error('Failed to resolve file URL:', error);
      return null;
    }
  }

  async downloadFile(fileId, fileName) {
    try {
      const fileData = await this.getFileData(fileId);
      
      // Convert base64 to blob
      const response = await fetch(fileData.data);
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName || fileData.name;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error('Failed to download file');
    }
  }

  async getStorageStats() {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.getAll();

      request.onsuccess = () => {
        const files = request.result;
        const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
        const totalFiles = files.length;
        
        resolve({
          totalFiles,
          totalSize,
          formattedSize: this.formatFileSize(totalSize)
        });
      };

      request.onerror = () => {
        reject(new Error('Failed to get storage stats'));
      };
    });
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async clearAllFiles(userId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const index = objectStore.index('userId');
      const request = index.openCursor(userId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to clear files'));
      };
    });
  }
}

// Create and export singleton instance
const localFileStorage = new LocalFileStorage();

export default localFileStorage;

// Export convenience functions
export const uploadFile = (file, userId, parayanaId) => 
  localFileStorage.uploadFile(file, userId, parayanaId);

export const getFiles = (userId, parayanaId) => 
  localFileStorage.getFiles(userId, parayanaId);

export const deleteFile = (fileId) => 
  localFileStorage.deleteFile(fileId);

export const downloadFile = (fileId, fileName) => 
  localFileStorage.downloadFile(fileId, fileName);

export const resolveFileURL = (url) => 
  localFileStorage.resolveFileURL(url);

export const getStorageStats = () => 
  localFileStorage.getStorageStats(); 